import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '../types/index.js';
import { ApiClient } from '../api/client.js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, pass: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  hasPermission: (module: string, action: string) => boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async () => {
    const token = ApiClient.getAccessToken();
    
    // Purge legacy mock tokens if present in localStorage
    if (!token || token === 'mock_admin_access_token') {
      ApiClient.clearTokens();
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const res = await ApiClient.get('/auth/me');
      if (res.success && res.data) {
        setUser(res.data);
      } else {
        ApiClient.clearTokens();
        setUser(null);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      ApiClient.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const login = async (email: string, pass: string) => {
    const cleanEmail = email.trim().toLowerCase();
    try {
      console.log('[Auth] Attempting login for:', cleanEmail);
      const res = await ApiClient.post('/auth/login', { email: cleanEmail, password: pass });
      
      if (res.success && res.data && res.data.accessToken) {
        console.log('[Auth] Login successful. Saving real JWT tokens.');
        ApiClient.setTokens(res.data.accessToken, res.data.refreshToken);
        setUser(res.data.user);
        return { success: true };
      }

      return { success: false, error: res.error || 'Invalid email or password.' };
    } catch (err: any) {
      console.error('[Auth] Login error:', err);
      return { success: false, error: err.message || 'Login failed.' };
    }
  };

  const logout = async () => {
    try { await ApiClient.post('/auth/logout'); } catch (e) {}
    ApiClient.clearTokens();
    setUser(null);
  };

  const logoutAll = async () => {
    try { await ApiClient.post('/auth/logout-all'); } catch (e) {}
    ApiClient.clearTokens();
    setUser(null);
  };

  const hasPermission = (module: string, action: string): boolean => {
    if (!user) return false;
    if (user.role?.code === 'owner' || user.role?.code === 'admin' || user.permissions?.includes('*')) return true;
    const requiredCode = `${module.toLowerCase()}:${action.toLowerCase()}`;
    return user.permissions?.includes(requiredCode) || false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        logoutAll,
        hasPermission,
        refreshProfile: fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
